import type { JourneyPhase } from "../../generated-api";
import type { JourneyPhaseKind } from "./types";

// Maps a route pathname to the journey phase it represents, so the chat
// sidebar can auto-align its active phase with whatever page the user is on.
// Only pages with an unambiguous 1:1 phase mapping are listed; everything
// else (home, videos, settings, ...) is left alone.
const PAGE_PHASE_ROUTES: { pattern: RegExp; kind: JourneyPhaseKind }[] = [
  { pattern: /^\/settings$/, kind: "profile_incomplete" },
  { pattern: /^\/goals\/edit$/, kind: "set_targets" },
  { pattern: /^\/targets\/[^/]+\/assess$/, kind: "assess_current_condition" },
  { pattern: /^\/programs\/new$/, kind: "generate_program" },
  { pattern: /^\/programs\/[^/]+\/edit$/, kind: "generate_program" },
];

export function getPagePhaseForPath(pathname: string): JourneyPhaseKind | null {
  const match = PAGE_PHASE_ROUTES.find((r) => r.pattern.test(pathname));
  return match?.kind ?? null;
}

// Reverse of the map above, for the two places the user explicitly picks a
// phase (the in-chat journey switcher, and the page-match banner's "back to
// default"): where should they land for that phase? Only phases with one
// unambiguous, parameter-free destination are listed; `assess_current_condition`
// (needs a target id) and `generate_program`/`adjust_program` for an existing
// program (needs a program id) are left out rather than guessing. `perform`,
// `adjust_program` (new-program case), and `assess_program_outcome` all land
// on the Home page, which surfaces the right loop card via `JourneyHero`.
const PHASE_PAGE_ROUTES: Partial<Record<JourneyPhaseKind, string>> = {
  profile_incomplete: "/settings",
  set_targets: "/goals/edit",
  generate_program: "/programs/new",
  perform: "/programs",
  adjust_program: "/programs",
  assess_program_outcome: "/programs",
};

export function getPathForPhase(kind: JourneyPhaseKind): string | null {
  return PHASE_PAGE_ROUTES[kind] ?? null;
}

// Like `getPathForPhase`, but phase-data-aware: `perform`, `adjust_program`,
// and `assess_program_outcome` all have an active `current_program` to go
// along with them, and that program's own page (where you'd actually train,
// adjust, or review it) is a better destination than the generic Home loop
// view — mirrors the `to` link `JourneyHero` uses for the same phases.
export function getDefaultPathForPhase(
  phase: JourneyPhase | null,
): string | null {
  if (!phase) return null;
  switch (phase.kind) {
    case "perform":
      return phase.current_program
        ? `/programs/${phase.current_program.id}`
        : getPathForPhase(phase.kind);
    case "adjust_program":
      return phase.current_program
        ? `/programs/${phase.current_program.id}/edit`
        : getPathForPhase(phase.kind);
    case "assess_program_outcome":
      return phase.current_program
        ? `/programs/assess/${phase.current_program.id}`
        : getPathForPhase(phase.kind);
    default:
      return getPathForPhase(phase.kind);
  }
}
